import { useEffect, useState } from "react";
import { Plus, Trash, Truck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import ExportButton from "../components/ExportButton";
import { api, currency, FUEL_LABELS, FUEL_COLORS, todayKey } from "../lib/api";
import { useStation } from "../lib/station";

const FUEL_TYPES = ["regular", "mid", "premium"];

export default function Fuel() {
  const { activeId } = useStation();
  const [tanks, setTanks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    if (!activeId) return;
    const [t, d] = await Promise.all([api.listTanks(activeId), api.listDeliveries(activeId)]);
    setTanks(t);
    setDeliveries(d);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId) return null;

  const handleDelete = async (id) => {
    await api.deleteDelivery(id);
    toast.success("Delivery removed");
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Fuel"
        subtitle={`${deliveries.length} delivery records`}
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="deliveries" testId="export-deliveries" />
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="add-delivery-btn"
              className="h-11 px-4 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] transition-colors font-bold text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> Delivery
            </button>
          </div>
        }
      />

      <div className="grid gap-3 mb-6">
        {tanks.map((t) => (
          <TankCard key={t.id} tank={t} />
        ))}
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Recent Deliveries</h2>
      <div className="grid gap-2" data-testid="delivery-list">
        {deliveries.length === 0 && (
          <div className="surface rounded-lg p-6 text-center text-sm text-gray-500">No deliveries logged yet.</div>
        )}
        {deliveries.map((d) => (
          <div key={d.id} className="surface rounded-lg p-3 flex items-center justify-between" data-testid={`delivery-row-${d.id}`}>
            <div className="flex items-center gap-3">
              <Truck size={22} weight="bold" style={{ color: FUEL_COLORS[d.fuel_type] }} />
              <div>
                <div className="text-sm font-bold text-white">
                  {Math.round(d.gallons).toLocaleString()}g · {FUEL_LABELS[d.fuel_type]}
                </div>
                <div className="text-xs text-gray-500">
                  {d.date} · {d.supplier} · {currency(d.cost_per_gallon)}/gal
                </div>
              </div>
            </div>
            <button
              onClick={() => handleDelete(d.id)}
              data-testid={`delete-delivery-${d.id}`}
              className="h-9 w-9 grid place-items-center rounded-lg text-gray-500 hover:text-[#EF4444] hover:bg-white/[0.04]"
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      {showForm && (
        <DeliveryForm
          stationId={activeId}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function TankCard({ tank }) {
  const pct = Math.max(0, Math.min(100, (tank.current_gallons / tank.capacity_gallons) * 100));
  const isCritical = pct < tank.low_threshold_pct;
  const fill = isCritical ? "#EF4444" : pct < tank.low_threshold_pct * 1.5 ? "#F59E0B" : "#10B981";
  return (
    <div className="surface rounded-lg p-4" data-testid={`tank-card-${tank.fuel_type}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: FUEL_COLORS[tank.fuel_type] }} />
          <span className="font-bold text-white text-sm uppercase tracking-wider">{FUEL_LABELS[tank.fuel_type]}</span>
          {isCritical && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#EF4444]">
              <Warning size={12} weight="fill" /> Low
            </span>
          )}
        </div>
        <span className="font-display font-black text-2xl" style={{ color: fill }}>
          {pct.toFixed(0)}%
        </span>
      </div>
      <div className="h-3 rounded-full bg-[#0F1115] border border-white/[0.06] overflow-hidden">
        <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: fill }} />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-400 font-mono">
        <span>{Math.round(tank.current_gallons).toLocaleString()} gal</span>
        <span>/ {Math.round(tank.capacity_gallons).toLocaleString()} cap</span>
      </div>
    </div>
  );
}

function DeliveryForm({ stationId, onClose, onCreated }) {
  const [date, setDate] = useState(todayKey());
  const [fuelType, setFuelType] = useState("regular");
  const [gallons, setGallons] = useState("");
  const [supplier, setSupplier] = useState("");
  const [cost, setCost] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!gallons || !supplier || !cost) {
      toast.error("Fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.createDelivery({
        station_id: stationId,
        date,
        fuel_type: fuelType,
        gallons: parseFloat(gallons),
        supplier,
        cost_per_gallon: parseFloat(cost),
      });
      toast.success("Delivery logged");
      onCreated();
    } catch {
      toast.error("Failed to log delivery");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Log Delivery" testId="delivery-modal">
      <form onSubmit={submit} className="grid gap-3" data-testid="delivery-form">
        <Field label="Date">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-dark" data-testid="delivery-date" required />
        </Field>
        <Field label="Fuel Type">
          <div className="grid grid-cols-3 gap-2">
            {FUEL_TYPES.map((f) => (
              <button
                type="button"
                key={f}
                onClick={() => setFuelType(f)}
                data-testid={`delivery-fuel-${f}`}
                className={`h-12 rounded-lg text-sm font-bold uppercase tracking-wider transition-colors ${
                  fuelType === f ? "bg-[#FF6B00] text-white" : "surface text-gray-300 hover:border-white/20"
                }`}
              >
                {f === "mid" ? "Mid" : f}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Gallons">
          <input type="number" step="0.01" min="0" value={gallons} onChange={(e) => setGallons(e.target.value)} placeholder="e.g. 3000" className="input-dark" data-testid="delivery-gallons" required />
        </Field>
        <Field label="Supplier">
          <input type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. Marathon Petroleum" className="input-dark" data-testid="delivery-supplier" required />
        </Field>
        <Field label="Cost Per Gallon ($)">
          <input type="number" step="0.001" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 2.95" className="input-dark" data-testid="delivery-cost" required />
        </Field>
        <button type="submit" disabled={submitting} data-testid="delivery-submit" className="h-14 mt-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 font-bold uppercase tracking-wider text-white">
          {submitting ? "Saving..." : "Save Delivery"}
        </button>
      </form>
    </Modal>
  );
}

export function Field({ label, children }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-500">{label}</span>
      {children}
    </label>
  );
}

export function Modal({ title, children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div className="w-full max-w-md surface rounded-t-2xl sm:rounded-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black uppercase tracking-tight text-2xl">{title}</h2>
          <button onClick={onClose} className="h-9 w-9 grid place-items-center rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.04]" data-testid="modal-close">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
